import {
  Flex,
  Text,
  Box,
  FormControl,
  FormErrorMessage,
  Input,
} from "@chakra-ui/react";
import {
  useCreateDocument,
  useGetDetailDocument,
  useUpdateDocument,
} from "../../hooks/useDocument";
import { t } from "i18next";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import * as yup from "yup";
import EInput from "components/EInput";
import ETextarea from "components/ETextarea";
import { useForm, useWatch, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import PrimaryButton from "components/Buttons/PrimaryButton";
import SecondaryButton from "components/Buttons/SecondaryButton";
import { useEffect, useState, useMemo } from "react";
import { PATHNAME } from "constants";
import { useParams } from "react-router-dom";
import { OPTIONS_DOCUMENT_TYPE } from "constants";
import {
  DOCUMENT_TYPE_OVERALL_TRAINING,
  DOCUMENT_TYPE_INDIVIDUAL_TRAINING,
} from "constants";
import ESelect from "components/ESelect";
import { translateNameOption } from "utils";
import { useListTargetGroup } from "hooks/useTargetGroup";
import ReactDatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ja from "date-fns/locale/ja";
import { useMasterData } from "hooks/useMasterData";

registerLocale("ja", ja);

const DocumentForm = () => {
  const params = useParams();
  const currentUrl = location.pathname;
  const isCreatePage = currentUrl === PATHNAME.CREATE_DOCUMENT;
  const mutationCreate = useCreateDocument();
  const mutationUpdate = useUpdateDocument();
  const { data } = useGetDetailDocument(params.id, !isCreatePage);
  const { data: listTargetGroup } = useListTargetGroup({ type: 2 });
  const [targetGroups, setTargetGroups] = useState([]);
  const [typeGroup, setTypeGroup] = useState(false);
  const [selectedTargetGroupId, setSelectedTargetGroupId] = useState(null);
  const { data: masterData } = useMasterData();

  const departmentLayer2Options = useMemo(() => {
    if (!masterData?.deparment_layer_2) return [];

    const mapped = masterData.deparment_layer_2.map((item) => ({
      id: item.yst_id,
      name: item.name,
    }));

    return [
      ...mapped,
    ];
  }, [masterData?.deparment_layer_2]);

  const jobOptions = useMemo(() => {
    if (!masterData?.jobs) return [];

    const mapped = masterData.jobs.map((item) => ({
      id: item.yst_id,
      name: item.name,
    }));

    return [
      ...mapped,
    ];
  }, [masterData?.jobs]);

  const onSubmit = (data) => {
    const selectedType = Number(data.type);
    const isOverall = selectedType === DOCUMENT_TYPE_OVERALL_TRAINING;

    const submitData = {
      ...data,
      target_group_id: isOverall
        ? null
        : data.target_group_id
        ? Number(data.target_group_id)
        : null,
      department_ids: isOverall
        ? []
        : Array.isArray(data.department_ids)
        ? data.department_ids.filter(Boolean).map((item) => Number(item))
        : data.department_ids
        ? [Number(data.department_ids)]
        : [],
      yst_job_ids: isOverall
        ? []
        : Array.isArray(data.yst_job_ids)
        ? data.yst_job_ids.filter(Boolean).map((item) => Number(item))
        : data.yst_job_ids
        ? [Number(data.yst_job_ids)]
        : [],
      type:
        selectedType === DOCUMENT_TYPE_OVERALL_TRAINING ||
        selectedType === DOCUMENT_TYPE_INDIVIDUAL_TRAINING
          ? data.type
          : null,
      fiscal_year_jp: data.fiscal_year_jp
        ? `${data.fiscal_year_jp.getFullYear()}-${String(
            data.fiscal_year_jp.getMonth() + 1
          ).padStart(2, "0")}`
        : null,
    };

    if (isCreatePage) {
      mutationCreate.mutate(submitData);
    } else {
      mutationUpdate.mutate({ ...submitData, id: params.id });
    }
  };

  useEffect(() => {
    if (data) {
      reset({
        name: data.result.data.name,
        description: data.result.data.description,
        course_code: data.result.data.course_code,
        type: data?.result?.data?.type ?? null,
        target_group_id: data.result?.data?.target_group_id ?? null,
        fiscal_year_jp: data.result?.data?.fiscal_year_jp
          ? new Date(data.result.data.fiscal_year_jp + "-01")
          : null,
        department_ids:
          data.result?.data?.department_ids?.map((item) => String(item)) ?? [],
        yst_job_ids:
          data.result?.data?.yst_job_ids?.map((item) => String(item)) ?? [],
      });
    }
  }, [data?.result?.data]);

  const schema = useMemo(() => {
    const isIndividual = typeGroup === DOCUMENT_TYPE_INDIVIDUAL_TRAINING;
    const targetGroupNumber = Number(selectedTargetGroupId);
    const requiresDepartment =
      isIndividual && (targetGroupNumber === 10 || targetGroupNumber === 11);
    const requiresJob = isIndividual && targetGroupNumber === 11;

    return yup.object().shape({
      name: yup
        .string()
        .required(t("messages.required"))
        .max(255, t("messages.common_e002")),
      course_code: yup.string().required(t("messages.required")),
      description: yup.string().nullable().max(1000, t("messages.common_e003")),
      target_group_id: isIndividual
        ? yup
            .number()
            .required(t("messages.required"))
            .notOneOf([0], t("messages.required"))
        : yup.number().notRequired(),
      department_ids: requiresDepartment
        ? yup
            .array()
            .min(1, t("messages.required"))
            .required(t("messages.required"))
        : yup.array().notRequired(),
      yst_job_ids: requiresJob
        ? yup
            .array()
            .min(1, t("messages.required"))
            .required(t("messages.required"))
        : yup.array().notRequired(),
      fiscal_year_jp: yup.date().required(t("messages.required")),
    });
  }, [selectedTargetGroupId, t, typeGroup]);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      description: "",
      course_code: "",
      type: null,
      target_group_id: null,
      fiscal_year_jp: isCreatePage ? new Date() : null,
      department_ids: [],
      yst_job_ids: [],
    },
    resolver: yupResolver(schema),
  });

  const selectedType = useWatch({
    control,
    name: "type",
  });
  const selectedTargetGroup = useWatch({
    control,
    name: "target_group_id",
  });

  useEffect(() => {
    setTypeGroup(+control._formValues.type);
  }, [control._formValues.type]);

  useEffect(() => {
    setSelectedTargetGroupId(+control._formValues.target_group_id);
  }, [control._formValues.target_group_id]);

  const showDepartmentLayer2 =
    Number(selectedType) === DOCUMENT_TYPE_INDIVIDUAL_TRAINING &&
    (Number(selectedTargetGroup) === 10 || Number(selectedTargetGroup) === 11);
  const showJobField =
    Number(selectedType) === DOCUMENT_TYPE_INDIVIDUAL_TRAINING &&
    Number(selectedTargetGroup) === 11;

  useEffect(() => {
    if (!showDepartmentLayer2) {
      setValue("department_ids", []);
    }
    if (!showJobField) {
      setValue("yst_job_ids", []);
    }
  }, [setValue, showDepartmentLayer2, showJobField]);

  useEffect(() => {
    if (listTargetGroup?.result?.data) {
      const targetGroups = [...listTargetGroup.result.data].sort(
        (a, b) => (a.order_number ?? 0) - (b.order_number ?? 0)
      );
      setTargetGroups([
        {
          id: 0,
          name: t("common.option.placeholder"),
        },
        ...targetGroups,
      ]);
    }
  }, [listTargetGroup?.result?.data]);

  return (
    <Flex direction="column" pt={{ base: "120px", md: "40px" }}>
      <Card overflowx={{ sm: "hidden", xl: "hidden" }} pb="0px">
        <CardHeader p="6px 0px 22px 0px">
          <Flex justifyContent={"space-between"}>
            <Text fontSize="xl" fontWeight="bold">
              {isCreatePage
                ? t("pages.document.title_create")
                : t("pages.document.title_edit")}
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex gap={"10px"} align={"center"} mb={"20px"}>
              <Box w={"15%"}>
                <Text fontSize={"14px"}>
                  {t("pages.document.type")}
                  <Text as="span" color="red.500"> *</Text>
                </Text>
              </Box>
              <Box width={"50%"}>
                <ESelect
                  name="type"
                  control={control}
                  dataSelect={translateNameOption(OPTIONS_DOCUMENT_TYPE)}
                />
              </Box>
            </Flex>
            {Number(selectedType) === DOCUMENT_TYPE_INDIVIDUAL_TRAINING && (
              <Flex gap={"10px"} align={"center"} mb={"20px"}>
                <Box w={"15%"}>
                  <Text fontSize={"14px"}>
                    {t("pages.document.target_group_id")}
                    <Text as="span" color="red.500"> *</Text>
                  </Text>
                </Box>
                <Box width={"50%"}>
                  <ESelect
                    name="target_group_id"
                    control={control}
                    dataSelect={targetGroups}
                  />
                </Box>
              </Flex>
            )}
            {Number(selectedType) === DOCUMENT_TYPE_INDIVIDUAL_TRAINING && (
              <>
                {showDepartmentLayer2 && (
                  <Flex gap={"10px"} align={"center"} mb={"20px"}>
                    <Box w={"15%"}>
                      <Text fontSize={"14px"}>
                        対象第2階層
                        <Text as="span" color="red.500"> *</Text>
                      </Text>
                    </Box>
                    <Box width={"50%"}>
                      <ESelect
                        name="department_ids"
                        control={control}
                        dataSelect={departmentLayer2Options}
                        isMulti
                        placeholder={t("common.option.department_layer_2_placeholder")}
                      />
                    </Box>
                  </Flex>
                )}
                {showJobField && (
                  <Flex gap={"10px"} align={"center"} mb={"20px"}>
                    <Box w={"15%"}>
                      <Text fontSize={"14px"}>
                        対象職種
                        <Text as="span" color="red.500"> *</Text>
                      </Text>
                    </Box>
                    <Box width={"50%"}>
                      <ESelect
                        name="yst_job_ids"
                        control={control}
                        dataSelect={jobOptions}
                        isMulti
                        placeholder={t("common.option.job_placeholder")}
                      />
                    </Box>
                  </Flex>
                )}
              </>
            )}
            <Flex gap={"10px"} align={"center"} mb={"20px"}>
              <Box w={"15%"}>
                <Text fontSize={"14px"}>
                  {t("pages.document.name")}
                  <Text as="span" color="red.500"> *</Text>
                </Text>
              </Box>
              <Box width={"50%"}>
                <EInput name="name" placeholder="" control={control} />
              </Box>
            </Flex>
            <Flex gap={"10px"} align={"center"} mb={"20px"}>
              <Box w={"15%"}>
                <Text fontSize={"14px"}>
                  {t("pages.document.course_id")}
                  <Text as="span" color="red.500"> *</Text>
                </Text>
              </Box>
              <Box width={"50%"}>
                <EInput name="course_code" placeholder="" control={control} />
              </Box>
            </Flex>
            <Flex gap={"10px"} align={"center"} mb={"20px"}>
              <Box w={"15%"}>
                <Text fontSize={"14px"}>
                  {t("pages.document.fiscal_year")}
                  <Text as="span" color="red.500"> *</Text>
                </Text>
              </Box>
              <Box width={"50%"}>
                <Controller
                  control={control}
                  name="fiscal_year_jp"
                  render={({ field, fieldState: { error } }) => (
                    <FormControl isInvalid={!!error}>
                      <ReactDatePicker
                        selected={field.value}
                        onChange={(date) => field.onChange(date)}
                        showMonthYearPicker
                        dateFormat={t("common.option.date_format")}
                        customInput={<Input fontSize={"14px"} />}
                        locale="ja"
                        placeholderText={t("common.option.date_placeholder")}
                      />
                      <FormErrorMessage fontSize={"14px"}>
                        {error && error.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                />
              </Box>
            </Flex>
            <Flex gap={"10px"} mb={"20px"}>
              <Box w={"15%"} mt={"8px"}>
                <Text fontSize={"14px"}>{t("pages.document.description")}</Text>
              </Box>
              <Box width={"50%"}>
                <ETextarea
                  name="description"
                  placeholder=""
                  control={control}
                />
              </Box>
            </Flex>
            <Flex justifyContent={"end"} gap={"20px"} paddingY={"30px"}>
              <PrimaryButton
                text={
                  isCreatePage
                    ? t("pages.document.submit_create")
                    : t("pages.document.submit_update")
                }
                type="submit"
              />
              <SecondaryButton
                text={t("pages.document.cancel")}
                onClick={() => history.go(-1)}
              />
            </Flex>
          </form>
        </CardBody>
      </Card>
    </Flex>
  );
};

export default DocumentForm;
